import * as ContextMenuPrimitive from "@radix-ui/react-context-menu";
import * as DropdownMenuPrimitive from "@radix-ui/react-dropdown-menu";
import { Slot } from "@radix-ui/react-slot";
import clsx from "clsx";
import { createPluggable } from "pluggable-react";
import * as React from "react";
import { withRefProp } from "../../../utils/withRefProp";
import FloatingPanel from "../FloatingPanel/FloatingPanel";
import {
  DEFAULT_FLOATING_ALIGN_OFFSET,
  DEFAULT_FLOATING_SIDE_OFFSET,
  resolveFloatingPlacement,
} from "../FloatingPanel/positioning";
import Icon from "../Icon/Icon";
import Kbd, { type KbdProps } from "../Kbd/Kbd";
import Pressable from "../Pressable/Pressable";
import styles from "./DropdownMenu.module.scss";

type MenuMode = "dropdown" | "context";
const MenuModeContext = React.createContext<MenuMode | null>(null);

const DefaultDropdownMenu = ({
  contextMenu = false,
  children,
  ...props
}: React.ComponentPropsWithoutRef<typeof DropdownMenuPrimitive.Root> & { contextMenu?: boolean }) => {
  const mode: MenuMode = contextMenu ? "context" : "dropdown";
  return (
    <MenuModeContext.Provider value={mode}>
      {mode === "context" ? (
        <ContextMenuPrimitive.Root {...(props as React.ComponentPropsWithoutRef<typeof ContextMenuPrimitive.Root>)}>
          {children}
        </ContextMenuPrimitive.Root>
      ) : (
        <DropdownMenuPrimitive.Root {...props}>{children}</DropdownMenuPrimitive.Root>
      )}
    </MenuModeContext.Provider>
  );
};
const DropdownMenu = createPluggable("deci.dropdownMenu", DefaultDropdownMenu);

interface DropdownMenuTriggerProps extends React.ComponentPropsWithoutRef<typeof DropdownMenuPrimitive.Trigger> {
  ref?: React.ComponentPropsWithRef<typeof DropdownMenuPrimitive.Trigger>["ref"];
}

const DefaultDropdownMenuTrigger = (props: DropdownMenuTriggerProps) => {
  const mode = React.useContext(MenuModeContext);
  const TriggerComponent = mode === "context" ? ContextMenuPrimitive.Trigger : DropdownMenuPrimitive.Trigger;
  return <TriggerComponent {...props} />;
};
const DropdownMenuTrigger = createPluggable("deci.dropdownMenu.trigger", DefaultDropdownMenuTrigger);

const DefaultDropdownMenuGroup = (props: React.ComponentPropsWithoutRef<typeof DropdownMenuPrimitive.Group>) => {
  const mode = React.useContext(MenuModeContext);
  const GroupComponent = mode === "context" ? ContextMenuPrimitive.Group : DropdownMenuPrimitive.Group;
  return <GroupComponent {...(props as React.ComponentPropsWithoutRef<typeof GroupComponent>)} />;
};
const DropdownMenuGroup = createPluggable("deci.dropdownMenu.group", DefaultDropdownMenuGroup);

const DefaultDropdownMenuPortal = (props: React.ComponentPropsWithoutRef<typeof DropdownMenuPrimitive.Portal>) => {
  const mode = React.useContext(MenuModeContext);
  const PortalComponent = mode === "context" ? ContextMenuPrimitive.Portal : DropdownMenuPrimitive.Portal;
  return <PortalComponent {...(props as React.ComponentPropsWithoutRef<typeof PortalComponent>)} />;
};
const DropdownMenuPortal = createPluggable("deci.dropdownMenu.portal", DefaultDropdownMenuPortal);

const DefaultDropdownMenuSub = (props: React.ComponentPropsWithoutRef<typeof DropdownMenuPrimitive.Sub>) => {
  const mode = React.useContext(MenuModeContext);
  const SubComponent = mode === "context" ? ContextMenuPrimitive.Sub : DropdownMenuPrimitive.Sub;
  return <SubComponent {...(props as React.ComponentPropsWithoutRef<typeof SubComponent>)} />;
};
const DropdownMenuSub = createPluggable("deci.dropdownMenu.sub", DefaultDropdownMenuSub);

const DefaultDropdownMenuRadioGroup = (
  props: React.ComponentPropsWithoutRef<typeof DropdownMenuPrimitive.RadioGroup>,
) => {
  const mode = React.useContext(MenuModeContext);
  const RadioGroupComponent = mode === "context" ? ContextMenuPrimitive.RadioGroup : DropdownMenuPrimitive.RadioGroup;
  return <RadioGroupComponent {...(props as React.ComponentPropsWithoutRef<typeof RadioGroupComponent>)} />;
};
const DropdownMenuRadioGroup = createPluggable("deci.dropdownMenu.radioGroup", DefaultDropdownMenuRadioGroup);

interface DropdownMenuSubTriggerProps extends React.ComponentPropsWithoutRef<typeof DropdownMenuPrimitive.SubTrigger> {
  inset?: boolean;
  ref?: React.ComponentPropsWithRef<typeof DropdownMenuPrimitive.SubTrigger>["ref"];
}

const DefaultDropdownMenuSubTrigger = withRefProp(({ className, inset, children, ref, ...props }: DropdownMenuSubTriggerProps) => {
  const mode = React.useContext(MenuModeContext);
  const classNames = clsx(styles.subTrigger, className);
  const SubTriggerComponent = mode === "context" ? ContextMenuPrimitive.SubTrigger : DropdownMenuPrimitive.SubTrigger;
  return (
    <SubTriggerComponent
      ref={ref}
      className={classNames}
      data-inset={inset ? "true" : "false"}
      {...(props as React.ComponentPropsWithoutRef<typeof SubTriggerComponent>)}
    >
      {children}
      <Icon
        name="go-forward"
        className={clsx(styles.subChevron)}
      />
    </SubTriggerComponent>
  );
});
const DropdownMenuSubTrigger = createPluggable("deci.dropdownMenu.subTrigger", DefaultDropdownMenuSubTrigger);

interface DropdownMenuSubContentProps extends React.ComponentPropsWithoutRef<typeof DropdownMenuPrimitive.SubContent> {
  ref?: React.ComponentPropsWithRef<typeof DropdownMenuPrimitive.SubContent>["ref"];
}

const DefaultDropdownMenuSubContent = withRefProp(({
  className,
  children,
  asChild,
  ref,
  sideOffset = DEFAULT_FLOATING_SIDE_OFFSET,
  alignOffset = DEFAULT_FLOATING_ALIGN_OFFSET,
  ...props
}: DropdownMenuSubContentProps) => {
  const mode = React.useContext(MenuModeContext);
  const PortalComponent = mode === "context" ? ContextMenuPrimitive.Portal : DropdownMenuPrimitive.Portal;
  const SubContentComponent = mode === "context" ? ContextMenuPrimitive.SubContent : DropdownMenuPrimitive.SubContent;
  const placement = resolveFloatingPlacement((props as any).side, (props as any).align);
  return (
    <PortalComponent>
      <SubContentComponent
        asChild={true}
        ref={ref}
        className={clsx(styles.subContent, className)}
        sideOffset={sideOffset}
        alignOffset={alignOffset}
        {...(props as React.ComponentPropsWithoutRef<typeof SubContentComponent>)}
      >
        <FloatingPanel
          asChild={asChild}
          open={true}
          placement={placement}
        >
          {children}
        </FloatingPanel>
      </SubContentComponent>
    </PortalComponent>
  );
});
const DropdownMenuSubContent = createPluggable("deci.dropdownMenu.subContent", DefaultDropdownMenuSubContent);

interface DropdownMenuContentProps extends React.ComponentPropsWithoutRef<typeof DropdownMenuPrimitive.Content> {
  ref?: React.ComponentPropsWithRef<typeof DropdownMenuPrimitive.Content>["ref"];
}

const DefaultDropdownMenuContent = withRefProp(({
  className,
  sideOffset = DEFAULT_FLOATING_SIDE_OFFSET,
  alignOffset = DEFAULT_FLOATING_ALIGN_OFFSET,
  asChild,
  children,
  ref,
  ...props
}: DropdownMenuContentProps) => {
  const mode = React.useContext(MenuModeContext);
  const PortalComponent = mode === "context" ? ContextMenuPrimitive.Portal : DropdownMenuPrimitive.Portal;
  const ContentComponent = mode === "context" ? ContextMenuPrimitive.Content : DropdownMenuPrimitive.Content;
  const placement = resolveFloatingPlacement((props as any).side, (props as any).align);
  if (!mode) {
    return (
      <FloatingPanel
        ref={ref}
        asChild={asChild}
        open={true}
        placement={placement}
        className={clsx(styles.content, className)}
        {...props}
      >
        {children}
      </FloatingPanel>
    );
  }

  const contentProps =
    mode === "context"
      ? ({ sideOffset, alignOffset, ...props } as React.ComponentPropsWithoutRef<typeof ContextMenuPrimitive.Content>)
      : ({ sideOffset, alignOffset, ...props } as React.ComponentPropsWithoutRef<typeof DropdownMenuPrimitive.Content>);

  return (
    <PortalComponent>
      <ContentComponent
        asChild={true}
        ref={ref}
        className={clsx(styles.content, className)}
        {...(contentProps as React.ComponentPropsWithoutRef<typeof ContentComponent>)}
      >
        <FloatingPanel
          asChild={asChild}
          open={true}
          placement={placement}
        >
          {children}
        </FloatingPanel>
      </ContentComponent>
    </PortalComponent>
  );
});
const DropdownMenuContent = createPluggable("deci.dropdownMenu.content", DefaultDropdownMenuContent);

interface DropdownMenuItemProps extends React.ComponentPropsWithoutRef<typeof DropdownMenuPrimitive.Item> {
  inset?: boolean;
  color?: "default" | "destructive";
  ref?: React.ComponentPropsWithRef<typeof DropdownMenuPrimitive.Item>["ref"];
}

const DefaultDropdownMenuItem = withRefProp((props: DropdownMenuItemProps) => {
  const { asChild, className, inset, color, onClick, style, ref, children, ...otherProps } = props;
  const mode = React.useContext(MenuModeContext);

  const classNames = clsx(styles.item, className);
  const ItemComponent = !mode
    ? (Slot as any)
    : mode === "context"
      ? ContextMenuPrimitive.Item
      : DropdownMenuPrimitive.Item;
  return (
    <ItemComponent
      asChild={true}
      ref={ref}
      className={classNames}
      data-color={color}
      data-inset={inset ? "true" : "false"}
      style={style}
      onClick={(e: any) => {
        e.stopPropagation();
        onClick?.(e);
      }}
      {...otherProps}
    >
      <Pressable asChild={asChild}>
        {asChild ? (
          children
        ) : (
          <>
            <Pressable.Ripple />
            {children}
          </>
        )}
      </Pressable>
    </ItemComponent>
  );
});
const DropdownMenuItem = createPluggable("deci.dropdownMenu.item", DefaultDropdownMenuItem);

interface DropdownMenuItemLabelProps extends React.HTMLAttributes<HTMLSpanElement> {
  ref?: React.Ref<HTMLSpanElement>;
}

const DefaultDropdownMenuItemLabel = withRefProp(({ className, ref, ...props }: DropdownMenuItemLabelProps) => {
  return (
    <span
      ref={ref}
      className={clsx(styles.itemLabel, className)}
      {...props}
    />
  );
});
const DropdownMenuItemLabel = createPluggable("deci.dropdownMenu.item.label", DefaultDropdownMenuItemLabel);

interface DropdownMenuItemIconProps extends React.HTMLAttributes<HTMLSpanElement> {
  ref?: React.Ref<HTMLSpanElement>;
}

const DefaultDropdownMenuItemIcon = withRefProp(({ className, ref, ...props }: DropdownMenuItemIconProps) => {
  return (
    <span
      ref={ref}
      className={clsx(styles.itemIcon, className)}
      {...props}
    />
  );
});
const DropdownMenuItemIcon = createPluggable("deci.dropdownMenu.item.icon", DefaultDropdownMenuItemIcon);

interface DropdownMenuCheckboxItemProps
  extends React.ComponentPropsWithoutRef<typeof DropdownMenuPrimitive.CheckboxItem> {
  ref?: React.ComponentPropsWithRef<typeof DropdownMenuPrimitive.CheckboxItem>["ref"];
}

const DefaultDropdownMenuCheckboxItem = withRefProp(({
  className,
  children,
  checked,
  disabled,
  ref,
  style,
  ...props
}: DropdownMenuCheckboxItemProps) => {
  const mode = React.useContext(MenuModeContext);
  const CheckboxItemComponent =
    mode === "context" ? ContextMenuPrimitive.CheckboxItem : DropdownMenuPrimitive.CheckboxItem;
  const ItemIndicatorComponent =
    mode === "context" ? ContextMenuPrimitive.ItemIndicator : DropdownMenuPrimitive.ItemIndicator;
  // Render the Radix primitive directly (matching RadioItem below) rather than nesting it inside
  // DropdownMenuItem — DropdownMenuItem renders its own live DropdownMenuPrimitive.Item under the
  // hood, and stacking two Radix menu-item primitives on one node via asChild breaks roving-focus
  // keyboard navigation (arrow keys would highlight the first item, then stop).
  return (
    <CheckboxItemComponent
      ref={ref}
      className={clsx(styles.item, className)}
      checked={checked}
      disabled={disabled}
      style={style}
      {...(props as React.ComponentPropsWithoutRef<typeof CheckboxItemComponent>)}
    >
      <Pressable.Ripple />
      {children}
      <DropdownMenuItemIcon>
        <ItemIndicatorComponent>
          <Icon name="selected" />
        </ItemIndicatorComponent>
      </DropdownMenuItemIcon>
    </CheckboxItemComponent>
  );
});
const DropdownMenuCheckboxItem = createPluggable("deci.dropdownMenu.checkboxItem", DefaultDropdownMenuCheckboxItem);

interface DropdownMenuRadioItemProps extends React.ComponentPropsWithoutRef<typeof DropdownMenuPrimitive.RadioItem> {
  ref?: React.ComponentPropsWithRef<typeof DropdownMenuPrimitive.RadioItem>["ref"];
}

const DefaultDropdownMenuRadioItem = withRefProp(({ className, children, ref, ...props }: DropdownMenuRadioItemProps) => {
  const mode = React.useContext(MenuModeContext);
  const RadioItemComponent = mode === "context" ? ContextMenuPrimitive.RadioItem : DropdownMenuPrimitive.RadioItem;
  const ItemIndicatorComponent =
    mode === "context" ? ContextMenuPrimitive.ItemIndicator : DropdownMenuPrimitive.ItemIndicator;
  return (
    <RadioItemComponent
      ref={ref}
      className={clsx(styles.radioItem, className)}
      {...(props as React.ComponentPropsWithoutRef<typeof RadioItemComponent>)}
    >
      <span>
        <ItemIndicatorComponent>
          <Icon name="radio-selected" />
        </ItemIndicatorComponent>
      </span>
      {children}
    </RadioItemComponent>
  );
});
const DropdownMenuRadioItem = createPluggable("deci.dropdownMenu.radioItem", DefaultDropdownMenuRadioItem);

interface DropdownMenuLabelProps extends React.ComponentPropsWithoutRef<typeof DropdownMenuPrimitive.Label> {
  inset?: boolean;
  ref?: React.ComponentPropsWithRef<typeof DropdownMenuPrimitive.Label>["ref"];
}

const DefaultDropdownMenuLabel = withRefProp(({ className, inset, ref, ...props }: DropdownMenuLabelProps) => {
  const mode = React.useContext(MenuModeContext);
  const classNames = clsx(styles.label, className);
  const LabelComponent = mode === "context" ? ContextMenuPrimitive.Label : DropdownMenuPrimitive.Label;
  return (
    <LabelComponent
      ref={ref}
      className={classNames}
      data-inset={inset ? "true" : "false"}
      {...(props as React.ComponentPropsWithoutRef<typeof LabelComponent>)}
    />
  );
});
const DropdownMenuLabel = createPluggable("deci.dropdownMenu.label", DefaultDropdownMenuLabel);

interface DropdownMenuSeparatorProps extends React.ComponentPropsWithoutRef<typeof DropdownMenuPrimitive.Separator> {
  ref?: React.ComponentPropsWithRef<typeof DropdownMenuPrimitive.Separator>["ref"];
}

const DefaultDropdownMenuSeparator = withRefProp(({ className, ref, ...props }: DropdownMenuSeparatorProps) => {
  const mode = React.useContext(MenuModeContext);
  const SeparatorComponent = mode === "context" ? ContextMenuPrimitive.Separator : DropdownMenuPrimitive.Separator;
  return (
    <SeparatorComponent
      ref={ref}
      className={clsx(styles.separator, className)}
      {...(props as React.ComponentPropsWithoutRef<typeof SeparatorComponent>)}
    />
  );
});
const DropdownMenuSeparator = createPluggable("deci.dropdownMenu.separator", DefaultDropdownMenuSeparator);

export interface DropdownMenuShortcutProps extends Omit<React.HTMLAttributes<HTMLSpanElement>, "color"> {
  keys?: KbdProps["keys"];
  variant?: KbdProps["variant"];
  color?: KbdProps["color"];
  delimiter?: KbdProps["delimiter"];
}

const DefaultDropdownMenuShortcut = ({
  className,
  children,
  keys,
  variant = "contained",
  color = "light",
  delimiter,
  ...props
}: DropdownMenuShortcutProps) => (
  <span
    className={clsx(styles.shortcut, className)}
    {...props}
  >
    {keys ? (
      <Kbd
        inline
        keys={keys}
        variant={variant}
        color={color}
        delimiter={delimiter}
      />
    ) : children}
  </span>
);
const DropdownMenuShortcut = createPluggable<DropdownMenuShortcutProps>("deci.dropdownMenu.shortcut", DefaultDropdownMenuShortcut);

export default Object.assign(DropdownMenu, {
  Trigger: DropdownMenuTrigger,
  Content: DropdownMenuContent,
  Item: Object.assign(DropdownMenuItem, {
    Label: DropdownMenuItemLabel,
    Icon: DropdownMenuItemIcon,
  }),
  CheckboxItem: DropdownMenuCheckboxItem,
  RadioItem: DropdownMenuRadioItem,
  Label: DropdownMenuLabel,
  Separator: DropdownMenuSeparator,
  Shortcut: DropdownMenuShortcut,
  Group: DropdownMenuGroup,
  Portal: DropdownMenuPortal,
  Sub: DropdownMenuSub,
  SubContent: DropdownMenuSubContent,
  SubTrigger: DropdownMenuSubTrigger,
  RadioGroup: DropdownMenuRadioGroup,
});
